import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { uploadFile, deleteFile } from '@/lib/storage'
import { toast } from '@/lib/toast'

export function useDocuments() {
  return useQuery({
    queryKey: ['my-documents'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return []
      const { data, error } = await supabase.from('resident_documents')
        .select('*').eq('resident_id', session.user.id).order('uploaded_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUploadDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { file: File; documentType: string; documentLabel?: string; documentNumber?: string; issuingAuthority?: string; issuedDate?: string; expiryDate?: string; notes?: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')
      const ext = input.file.name.split('.').pop() ?? 'bin'
      const path = `${session.user.id}/${crypto.randomUUID()}.${ext}`
      await uploadFile('resident-documents', path, input.file)
      const { error } = await supabase.from('resident_documents').insert({
        resident_id: session.user.id,
        document_type: input.documentType,
        document_label: input.documentLabel ?? null,
        document_number: input.documentNumber ?? null,
        issuing_authority: input.issuingAuthority ?? null,
        issued_date: input.issuedDate ?? null,
        expiry_date: input.expiryDate ?? null,
        notes: input.notes ?? null,
        storage_path: path,
      })
      if (error) {
        // Cleanup orphan file
        try { await deleteFile('resident-documents', path) } catch { /* ignore */ }
        throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-documents'] })
      toast.success('Document uploaded. Your barangay admin will verify it.')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useDeleteDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, storagePath }: { id: string; storagePath: string | null }) => {
      const { error } = await supabase.from('resident_documents').delete().eq('id', id)
      if (error) throw error
      if (storagePath) {
        try { await deleteFile('resident-documents', storagePath) } catch { /* ignore */ }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-documents'] })
      toast.success('Document removed')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
